import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Shield, Clock, User, Trash2, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authGet, authPut, authDelete } from '../../utils/auth';
import useRequireAdmin from '../../hooks/useRequireAdmin';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const UserManagement = () => {
  // Server-verified admin gate; `currentUser` is the verified identity.
  const { user: currentUser, checking } = useRequireAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Pending role change awaiting confirmation: { userId, userName, newRole }.
  const [roleTarget, setRoleTarget] = useState(null);
  const [roleChanging, setRoleChanging] = useState(false);
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      const usersResponse = await authGet('/admin/users');
      setUsers(usersResponse.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, []);

  // Only fetch once the admin session is server-verified.
  useEffect(() => {
    if (currentUser) fetchUsers();
  }, [currentUser, fetchUsers]);

  // Optimistically update the row, then reconcile with the server on error.
  const applyRoleChange = async (userId, newRole) => {
    const prevUsers = users;
    setUsers(prev => prev.map(u =>
      u.user_id === userId ? { ...u, role: newRole, access_requested: false } : u
    ));
    try {
      await authPut(`/admin/users/${userId}/role`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      // Revert the optimistic change and resync with server truth.
      setUsers(prevUsers);
      toast.error(error.response?.data?.detail || 'Failed to update role');
      fetchUsers();
    }
  };

  // Confirm before changing a role (especially promotion to admin).
  const requestRoleChange = (userId, userName, newRole, currentRole) => {
    if (newRole === currentRole) return;
    setRoleTarget({ userId, userName, newRole });
  };

  const confirmRoleChange = async () => {
    if (!roleTarget) return;
    setRoleChanging(true);
    await applyRoleChange(roleTarget.userId, roleTarget.newRole);
    setRoleChanging(false);
    setRoleTarget(null);
  };

  const openDeleteDialog = (userId, userName) => {
    setDeleteTarget({ userId, userName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await authDelete(`/admin/users/${deleteTarget.userId}`);
      setUsers(prev => prev.filter(u => u.user_id !== deleteTarget.userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <User className="w-4 h-4 text-blue-600" />;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      user: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    };
    return styles[role] || styles.user;
  };

  // Separate users by status
  const pendingRequests = users.filter(u => u.role === 'pending' && u.access_requested);
  const admins = users.filter(u => u.role === 'admin');
  const regularUsers = users.filter(u => u.role === 'user');
  const pendingUsers = users.filter(u => u.role === 'pending' && !u.access_requested);

  if (checking || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/admin')} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">User Management</h1>
            <p className="text-sm text-muted-foreground">Manage user access and roles</p>
          </div>
        </div>

        {/* Pending Access Requests */}
        {pendingRequests.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg font-semibold text-yellow-800">
                Pending Access Requests ({pendingRequests.length})
              </h2>
            </div>
            <div className="space-y-3">
              {pendingRequests.map(user => (
                <div key={user.user_id} className="bg-card rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {user.picture && (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                    )}
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => requestRoleChange(user.user_id, user.name, 'admin', user.role)}
                      className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors inline-flex items-center gap-1"
                    >
                      <Check className="w-4 h-4" />
                      Approve as Admin
                    </button>
                    <button
                      onClick={() => requestRoleChange(user.user_id, user.name, 'user', user.role)}
                      className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors inline-flex items-center gap-1"
                    >
                      <Check className="w-4 h-4" />
                      Approve as User
                    </button>
                    <button
                      onClick={() => openDeleteDialog(user.user_id, user.name)}
                      className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors inline-flex items-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Users Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">All Users ({users.length})</h2>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map(user => (
                  <tr key={user.user_id} className="hover:bg-muted/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <span className="font-medium">{user.name}</span>
                        {user.user_id === currentUser?.user_id && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">You</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getRoleBadge(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.access_requested && user.role === 'pending' ? (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded">Access Requested</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Active</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {user.user_id !== currentUser?.user_id && (
                        <div className="flex items-center justify-end gap-2">
                          <select
                            value={user.role}
                            onChange={(e) => requestRoleChange(user.user_id, user.name, e.target.value, user.role)}
                            className="text-sm border border-border rounded px-2 py-1 bg-background"
                          >
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                            <option value="pending">Pending</option>
                          </select>
                          <button
                            onClick={() => openDeleteDialog(user.user_id, user.name)}
                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setDeleteTarget(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-foreground">
              Delete <strong>{deleteTarget?.userName}</strong>? This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={deleting} onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Change Confirmation Dialog */}
      <Dialog open={!!roleTarget} onOpenChange={(open) => { if (!open && !roleChanging) setRoleTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" strokeWidth={1.5} />
              Change Role
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-foreground">
              Change <strong>{roleTarget?.userName}</strong>&apos;s role to <strong>{roleTarget?.newRole}</strong>?
            </p>
            {roleTarget?.newRole === 'admin' && (
              <p className="mt-3 text-sm text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                Admins have full access to manage all data and users.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={roleChanging} onClick={() => setRoleTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmRoleChange} disabled={roleChanging}>
              {roleChanging ? 'Updating...' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
